import React from 'react';
import { AlarmFilters, AlarmSeverity, AlarmStatus, AlarmType } from '@/api/alarms/types';
import { Button } from '@/components/ui/button';
import { Search, Filter, X } from 'lucide-react';

interface FilterControlsProps {
  filters: AlarmFilters;
  onFilterChange: (filters: AlarmFilters) => void;
  onRefresh: () => void;
}

export function FilterControls({ filters, onFilterChange, onRefresh }: FilterControlsProps) {
  const handleSeverityChange = (severity: AlarmSeverity | undefined) => {
    onFilterChange({ ...filters, severity });
  };

  const handleStatusChange = (status: AlarmStatus | undefined) => {
    onFilterChange({ ...filters, status });
  };

  const handleTypeChange = (type: AlarmType | undefined) => {
    onFilterChange({ ...filters, type });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const deviceId = e.target.value || undefined;
    onFilterChange({ ...filters, deviceId });
  };

  const clearFilters = () => {
    onFilterChange({});
  };

  return (
    <div className="p-2 border-b border-light-grey">
      <div className="flex flex-col space-y-1.5">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
            <Search className="h-3.5 w-3.5 text-dark-grey" />
          </div>
          <input
            type="text"
            placeholder="Search by device ID..."
            className="block w-full pl-8 pr-2 py-1.5 border border-light-grey rounded-md text-xs placeholder-dark-grey focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-bg"
            value={filters.deviceId || ''}
            onChange={handleSearchChange}
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <FilterDropdown
            label="Severity"
            options={[
              { value: undefined, label: 'All Severities' },
              { value: 'critical', label: 'Critical' },
              { value: 'warning', label: 'Warning' },
              { value: 'info', label: 'Info' },
            ]}
            value={filters.severity}
            onChange={(value) => handleSeverityChange(value as AlarmSeverity | undefined)}
          />

          <FilterDropdown
            label="Status"
            options={[
              { value: undefined, label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'acknowledged', label: 'Acknowledged' },
              { value: 'resolved', label: 'Resolved' },
            ]}
            value={filters.status}
            onChange={(value) => handleStatusChange(value as AlarmStatus | undefined)}
          />

          <FilterDropdown
            label="Type"
            options={[
              { value: undefined, label: 'All Types' },
              { value: 'temperature', label: 'Temperature' },
              { value: 'communication', label: 'Communication' },
              { value: 'power', label: 'Power' },
              { value: 'sensor', label: 'Sensor' },
              { value: 'system', label: 'System' },
            ]}
            value={filters.type}
            onChange={(value) => handleTypeChange(value as AlarmType | undefined)}
          />

          {(filters.severity || filters.status || filters.type || filters.deviceId) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-600 hover:bg-gray-100 text-[10px] h-[26px] px-2"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface FilterDropdownProps {
  label: string;
  options: Array<{ value: string | undefined; label: string }>;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

function FilterDropdown({ label, options, value, onChange }: FilterDropdownProps) {
  return (
    <div className="relative inline-block text-left">
      <select
        className="appearance-none block w-full pl-2 pr-6 py-1 text-[10px] border border-light-grey rounded-md bg-bg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-gray-700"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        style={{ backgroundImage: 'none' }}
      >
        <option value="" disabled>
          {label}
        </option>
        {options.map((option) => (
          <option key={option.label} value={option.value || ''}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </div>
  );
}
